import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Form, Button, Row, Col, Card, Spinner, Alert, Badge, Accordion, ListGroup, ProgressBar } from 'react-bootstrap';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import './App.css';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

// --- Helper Component for Action Checklist ---
function ActionChecklist({ text }) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  // Split by number and period (e.g., "1. ", "2. ")
  const items = text.split(/\d\.\s/).filter(item => item.trim() !== '');

  const parseItem = (item) => {
    // Find text between asterisks for the title
    const titleMatch = item.match(/\*\*(.*?)\*\*/);
    const title = titleMatch ? titleMatch[1] : '';
    // The rest of the string is the description
    const description = item.replace(/\*\*(.*?)\*\*/, '').trim();

    return (
      <React.Fragment>
        <strong>{title}</strong>
        {description}
      </React.Fragment>
    );
  };

  return (
    <ListGroup variant="flush">
      {items.map((item, index) => (
        <ListGroup.Item key={index} className="d-flex align-items-start border-0 px-0" style={{ backgroundColor: 'transparent' }}>
          <i className="bi bi-check-circle-fill text-success me-3"></i>
          <div>{parseItem(item)}</div>
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
}

// --- Career Readiness Map Component ---
function CareerReadinessMap({ roadmap, handoffGaps }) {
  const [activeCategory, setActiveCategory] = useState('Certifications & Education');
  const [completedTasks, setCompletedTasks] = useState({});

  const categories = [
    { title: "Certifications & Education", type: "Certification", icon: "bi-patch-check-fill" },
    { title: "Proven Experience Projects", type: "Experience", icon: "bi-briefcase-fill" },
    { title: "Skill Development", type: "Skill Development", icon: "bi-bar-chart-line-fill" },
    { title: "Networking & Visibility", type: "Networking", icon: "bi-people-fill" },
  ];

  const waypointsByCategory = categories.map(cat => ({
    ...cat,
    waypoints: roadmap.phases.flatMap(p => p.waypoints).filter(wp => wp.type === cat.type)
  }));

  const totalTasks = waypointsByCategory.flatMap(c => c.waypoints).length;
  const completedCount = Object.values(completedTasks).filter(Boolean).length;
  const readinessScore = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  const handleTaskToggle = (waypointTitle) => {
    setCompletedTasks(prev => ({ ...prev, [waypointTitle]: !prev[waypointTitle] }));
  };

  const chartData = {
    labels: ['Readiness', 'Gaps'],
    datasets: [
      {
        data: [readinessScore, 100 - readinessScore],
        backgroundColor: ['#4CAF50', '#E0E0E0'],
        borderColor: ['#FFFFFF', '#FFFFFF'],
        borderWidth: 2,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  };

  const activeWaypoints = waypointsByCategory.find(c => c.title === activeCategory)?.waypoints || [];

  return (
    <div className="mt-5 pt-5 border-top">
      <div className="text-center">
        <h2 className="mb-3">Career Readiness Map</h2>
        <p className="lead text-muted">Your personal chart for navigating the journey to your destination.</p>
      </div>
      <Card className="card-coastal p-4 shadow-lg">
        <Row className="align-items-center">
          <Col xs={12} md={4} className="text-center">
            <div style={{ height: '200px', position: 'relative' }}>
              <Doughnut data={chartData} options={chartOptions} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '2.5rem',
                fontWeight: 'bold',
              }}>
                {`${readinessScore}%`}
              </div>
            </div>
            <h5 className="mt-3">Overall Readiness</h5>
          </Col>

          <Col xs={12} md={8}>
            <Row>
              {waypointsByCategory.map((cat, index) => (
                <Col key={index} xs={12} md={6} className="mb-3">
                  <Card 
                    className={`h-100 shadow-sm text-center p-3 card-coastal-interactive ${activeCategory === cat.title ? 'active-card' : ''}`}
                    onClick={() => setActiveCategory(cat.title)}
                  >
                    <i className={`bi ${cat.icon} fs-1 mb-2 ${activeCategory === cat.title ? 'text-primary' : 'text-muted'}`}></i>
                    <h6 className="mb-0">{cat.title}</h6>
                    <ProgressBar 
                      variant="success"
                      now={cat.waypoints.length > 0 ? (Object.keys(completedTasks).filter(t => cat.waypoints.some(wp => wp.title === t) && completedTasks[t]).length / cat.waypoints.length) * 100 : 0}
                      style={{height: '5px', marginTop: '10px'}}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <h4 className="mb-3">{activeCategory}</h4>
            <ListGroup>
              {activeWaypoints.length > 0 ? activeWaypoints.map((wp, index) => (
                <ListGroup.Item key={index} className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                  <div>
                    <Form.Check 
                      type="checkbox"
                      id={`waypoint-check-${index}`}
                      label={wp.title}
                      checked={!!completedTasks[wp.title]}
                      onChange={() => handleTaskToggle(wp.title)}
                      className="fw-bold fs-6"
                    />
                    {wp.is_priority_gap && 
                      <Badge pill bg="danger" className="ms-2">Priority Gap</Badge>
                    }
                  </div>
                  <Accordion>
                     <Accordion.Item eventKey="0" style={{border: 'none'}}>
                        <Accordion.Header style={{padding: '0.5rem'}}><span className="text-muted">Details</span></Accordion.Header>
                        <Accordion.Body>
                            <p><strong>What is this?</strong> {wp.brief.what_is_this}</p>
                            <p><strong>Why it matters for this role:</strong> {wp.brief.why_it_matters}</p>
                            <p><strong>How to get started:</strong> {wp.brief.how_to_get_started}</p>
                            <Alert variant="success" className="mt-3">
                              <Alert.Heading><i className="bi bi-lightbulb-fill"></i> How to Use It Now</Alert.Heading>
                              <ActionChecklist text={wp.brief.how_to_use_it_now} />
                            </Alert>
                        </Accordion.Body>
                     </Accordion.Item>
                  </Accordion>
                </ListGroup.Item>
              )) : <p className="text-muted">No items in this category for your selected path.</p>}
            </ListGroup>
          </Col>
        </Row>
      </Card>
    </div>
  );
}


// --- Dynamic Roadmap Component (Unchanged) ---
function DynamicRoadmap({ militaryCode, destination, setDestination, roadmap, loading, error, handleChartCourse }) {
  // ... (This component's code remains unchanged) ...
  const getWaypointIcon = (type) => {
    switch (type) {
      case 'Certification':
        return <i className="bi bi-patch-check-fill text-primary me-2"></i>;
      case 'Skill Development':
        return <i className="bi bi-bar-chart-line-fill text-secondary me-2"></i>;
      case 'Experience':
        return <i className="bi bi-briefcase-fill text-info me-2"></i>;
      case 'Networking':
        return <i className="bi bi-people-fill text-success me-2"></i>;
      default:
        return null;
    }
  };

  return (
    <div className="mt-5 pt-5 border-top">
      <div className="text-center">
        <h2 className="mb-3">Your Dynamic Roadmap</h2>
        <p className="lead text-muted">Choose a destination and let our AI chart your course.</p>
      </div>
      
      <Card className="card-coastal p-4 shadow-lg">
        <Row className="align-items-center text-center mb-4">
          <Col xs={5} md={5}>
            <div className="p-2 border rounded shadow-sm">
              <h6 className="mb-1">Port of Origin</h6>
              <p className="fw-bold mb-0">{militaryCode ? militaryCode.toUpperCase() : 'N/A'}</p>
            </div>
          </Col>

          <Col xs={2} md={2}>
            <div style={{ position: 'relative', borderTop: '3px dotted #64B5F6', margin: '0 10px' }}>
              <div style={{ position: 'absolute', right: '-10px', top: '50%', transform: 'translateY(-50%)', color: '#64B5F6' }}>
                <i className="bi bi-chevron-right fs-4"></i>
              </div>
            </div>
          </Col>

          <Col xs={5} md={5}>
            <Form.Group controlId="destination-input">
              <Form.Label className="visually-hidden">Destination Career</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Enter Destination..."
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-center mb-4">
          <Button variant="success" size="lg" onClick={handleChartCourse} disabled={loading || !militaryCode}>
            {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Chart My Course'}
          </Button>
        </div>

        {error && <Alert variant="danger" className="mt-3 text-center">{error}</Alert>}

        {roadmap && (
          <div className="mt-4">
            <Row>
              <Col xs={12} md={6} className="mb-4 mb-md-0">
                <h4>Phased Waypoints</h4>
                <Accordion defaultActiveKey="0">
                  {roadmap.phases.map((phase, index) => (
                    <div key={index} className="mb-3">
                      <h5 className="text-muted">{phase.title}</h5>
                      {phase.waypoints.map((waypoint, wp_index) => (
                        <Accordion.Item key={wp_index} eventKey={`${index}-${wp_index}`}>
                          <Accordion.Header>
                            {getWaypointIcon(waypoint.type)}
                            {waypoint.title}
                          </Accordion.Header>
                          <Accordion.Body>
                            <p><strong>What is this?</strong> {waypoint.brief.what_is_this}</p>
                            <p><strong>Why it matters for this role:</strong> {waypoint.brief.why_it_matters}</p>
                            <p><strong>How to get started:</strong> {waypoint.brief.how_to_get_started}</p>
                            <Alert variant="success" className="mt-3">
                              <Alert.Heading><i className="bi bi-lightbulb-fill"></i> How to Use It Now</Alert.Heading>
                              <p className="mb-0">{waypoint.brief.how_to_use_it_now}</p>
                            </Alert>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </div>
                  ))}
                </Accordion>
              </Col>
              <Col xs={12} md={6}>
                <h4>Translator's Brief</h4>
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header><i className="bi bi-person-check-fill text-success me-2"></i>Hiring Manager Focus</Accordion.Header>
                    <Accordion.Body>{roadmap.translators_brief.hiring_manager_focus}</Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="1">
                    <Accordion.Header><i className="bi bi-translate text-info me-2"></i>Key Terminology</Accordion.Header>
                    <Accordion.Body>{roadmap.translators_brief.key_terminology}</Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="2">
                    <Accordion.Header><i className="bi bi-tools text-secondary me-2"></i>Common Software & Tools</Accordion.Header>
                    <Accordion.Body>{roadmap.translators_brief.common_software}</Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </div>
  );
}

// --- Main Pathfinder Component ---
function Pathfinder() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [destination, setDestination] = useState('');
  const [roadmap, setRoadmap] = useState(null);
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [roadmapError, setRoadmapError] = useState(null);
  const [handoffGaps, setHandoffGaps] = useState(null);
  const [isHandoffActive, setIsHandoffActive] = useState(false);
  const [showReadinessMap, setShowReadinessMap] = useState(false); // New state for visibility
  const [actionPlanLoading, setActionPlanLoading] = useState(false);

  useEffect(() => {
    const handoffDataString = sessionStorage.getItem('navigatorHandoffData');
    if (handoffDataString) {
      try {
        const handoffData = JSON.parse(handoffDataString);
        if (handoffData.destination) {
          setDestination(handoffData.destination);
          setIsHandoffActive(true);
        }
        if (handoffData.gaps) {
          setHandoffGaps(handoffData.gaps);
        }
        sessionStorage.removeItem('navigatorHandoffData');
      } catch (e) {
        console.error("Error parsing handoff data:", e);
        sessionStorage.removeItem('navigatorHandoffData');
      }
    }
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    if (!isHandoffActive) {
      setRoadmap(null);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/pathfinder/${searchQuery}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data. Is the backend running and the military code correct?');
      }
      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChartCourse = async () => {
    if (!destination) {
      setRoadmapError('Please enter a destination career.');
      return;
    }
    setRoadmapLoading(true);
    setRoadmapError(null);
    setRoadmap(null);
    setShowReadinessMap(false); // Hide map on new course generation

    const formData = new FormData();
    formData.append('military_code', searchQuery);
    formData.append('destination_title', destination);
    if (handoffGaps) {
      formData.append('gaps', JSON.stringify(handoffGaps));
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generate-roadmap/`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('Failed to generate roadmap.');
      }
      const data = await response.json();
      setRoadmap(data);
    } catch (err) {
      setRoadmapError(err.message);
    } finally {
      setRoadmapLoading(false);
    }
  };

  const handleBuildReadinessMap = async () => {
    if (!roadmap) return;

    setActionPlanLoading(true);
    setRoadmapError(null);

    // Extract the full waypoint objects from the high-level roadmap
    const highLevelWaypoints = roadmap.phases.flatMap(p => p.waypoints);

    const formData = new FormData();
    formData.append('waypoints', JSON.stringify(highLevelWaypoints));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generate-readiness-map/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to generate the Career Readiness Map.');
      }

      const detailedData = await response.json();
      
      // Create a new roadmap structure with the detailed, classified waypoints
      const newRoadmap = {
        ...roadmap, // Keep the translator's brief
        phases: [{
          title: "Action Plan",
          waypoints: detailedData.waypoints // Use the new detailed waypoints
        }]
      };

      setRoadmap(newRoadmap);
      setShowReadinessMap(true);

    } catch (err) {
      setRoadmapError(err.message);
    } finally {
      setActionPlanLoading(false);
    }
  };

  return (
    <Container className="my-5">
      <img src="/lonesailorlogo.png" alt="Lone Sailor Logo" className="mb-4" style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '150px' }} />
      <h1 className="app-title text-center mb-2">The Pathfinder</h1>
      <p className="app-tagline text-center mb-5">You've stood the watch. Now, we have the watch. Let's find your way forward.</p>

      {isHandoffActive && (
        <Alert variant="light" className="text-center card-coastal-cta mb-4">
          <Alert.Heading>Welcome to the Pathfinder!</Alert.Heading>
          <p className="mb-0">
            Based on your analysis from the Navigator, let's build your plan to become a <strong>{destination}</strong>.
            <br />
            Just confirm your starting point below to continue.
          </p>
        </Alert>
      )}

      <Row className="justify-content-center mb-4">
        <Col md={8}>
          <Card className="p-3 card-coastal shadow-sm">
            <Form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
              <Row className="align-items-center">
                <Col xs={12} md={9}>
                  <Form.Group controlId="military-code-search">
                    <Form.Label className="visually-hidden">Enter Your Military Job Code</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Enter your Military Job Code or role (e.g. Infantryman, 9502, I33A)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={3} className="d-grid mt-2 mt-md-0">
                  <Button variant="primary" className="btn-primary-custom" type="submit" disabled={loading}>
                    {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Reveal My Skills'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {error && <Alert variant="danger" className="text-center">{error}</Alert>}

      {results && (
        <Alert variant="light" className="mt-4 border small text-center">
          <i className="bi bi-compass me-2" style={{ color: '#D4AF37' }}></i>
          <strong>AI Guidance:</strong> AI-generated analysis. Please review carefully. Use of this service is subject to our <a href="/legal" target="_blank" className="alert-link">Terms of Service and Privacy Policy</a>.
        </Alert>
      )}

      {results && (
        <div>
          <div className="text-center mb-5">
            <h2 className="mb-2">Your Core Strengths</h2>
            <h4 className="text-muted fw-normal mb-3">as a {results.military_role}</h4>
            <p className="lead text-muted">You're more than a job title. These are the valuable, in-demand skills you've proven in the field.</p>
            <Row>
              {results.core_strengths.map((strength, index) => (
                <Col xs={12} md={3} key={index} className="mb-3">
                  <Card className="h-100 shadow-sm card-coastal">
                    <Card.Body>
                      <Card.Title className="fw-bold">{strength.name}</Card.Title>
                      <Card.Text>{strength.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          <div className="text-center">
            <h2 className="mb-3">Explore Your Career Clusters</h2>
            <p className="lead text-muted">Your skills open doors to fields you may have never considered. Explore these clusters to find your fit.</p>
            <Row>
              {results.career_clusters.map((cluster, index) => (
                <Col xs={12} md={4} key={index} className="mb-4">
                  <Card className="h-100 shadow card-coastal text-center">
                    <Card.Body>
                      <i className={`bi ${cluster.icon} fs-1 text-primary mb-3`}></i>
                      <Card.Title className="fw-bold">{cluster.name}</Card.Title>
                      <Card.Text className="mb-4">{cluster.description}</Card.Text>
                      <div>
                        {cluster.jobs.map((job, jobIndex) => (
                          <Badge pill bg="light" text="dark" key={jobIndex} className="m-1 p-2">{job.title}</Badge>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          <DynamicRoadmap 
            militaryCode={searchQuery} 
            destination={destination}
            setDestination={setDestination}
            roadmap={roadmap}
            loading={roadmapLoading}
            error={roadmapError}
            handleChartCourse={handleChartCourse}
          />

          {/* Conditionally render the new map after the first roadmap is generated */}
          {roadmap && !showReadinessMap && (
            <div className="text-center mt-4">
              <Button variant="primary" size="lg" className="btn-primary-custom" onClick={handleBuildReadinessMap} disabled={actionPlanLoading}>
                {actionPlanLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Build My Action Plan'}
              </Button>
            </div>
          )}

          {roadmap && showReadinessMap && <CareerReadinessMap roadmap={roadmap} handoffGaps={handoffGaps} />}

        </div>
      )}

      {/* O*NET Attribution Footer */}
      {results && (
        <div className="text-center mt-5 pt-3 border-top">
          <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
            <img src="/images/onet-logo.png" alt="O*NET Web Services Logo" style={{ width: '130px', height: '60px', marginRight: '10px', verticalAlign: 'middle' }} />
            <p className="text-muted small d-inline-block" style={{ maxWidth: '600px', verticalAlign: 'middle' }}>
              This application incorporates information from O*NET Web Services by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA). O*NET® is a trademark of USDOL/ETA.
            </p>
          </a>
        </div>
      )}
    </Container>
  );
}

export default Pathfinder;
